import { engineComponent } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { BackgroundStripElement } from "../../CustomComponents/BackgroundStripElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { TeasingComponent } from "../../CustomComponents/TeasingComponent";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _outroDelay: FrameTimerEventEmitter;
    private _goToScene: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;

    private _wildContainer: ContainerElement;
    private _reelSwitch: SwitchElement;
    private _backgroundStrip: BackgroundStripElement;

    private _teasingBonus: TeasingBonus;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._goToScene = CreateFrameTimer("goToScene");
        this._goToScene.on(FrameTimerEvents.Complete, this.goToScene.bind(this));

        this._teasingBonus = new TeasingBonus();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WildContainer" && this.childs[key] instanceof ContainerElement) {
                    this._wildContainer = this.childs[key];
                }

                if (key === "BackgroundStrip" && this.childs[key] instanceof BackgroundStripElement) {
                    this._backgroundStrip = this.childs[key];
                }

                if (key === "ReelSwitch" && this.childs[key] instanceof SwitchElement) {
                    this._reelSwitch = this.childs[key];
                }

                if (key === "TeasingComponent" && this.childs[key] instanceof TeasingComponent) {
                    this._teasingBonus = this.childs[key];
                }

                if (key === "TeasingFront" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingFront: this.childs[key] });
                    }
                }

                if (key === "TeasingParticles" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ teasingParticles: this.childs[key] });
                    }
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels !== null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(this._symbols, this._paylines, this._noWinContainer);
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.addElementInstance({ wildContainer: this._wildContainer });
                            this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initIntro(): void {
        if (GameManager.gameResponse.totalWin > 0 || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
        } else {
            PlayList.stop("SND_BaseRecaploop");
            PlayList.loop("SND_BaseLoop");
        }

        //if not in freespins activate base animations
        //if in fs configurator, configurator set active scene by selected symbol in method startConfigurator()
        if (!ModuleManager.freespinsEnabled()) {
            if (this._backgroundStrip != null) {
                this._backgroundStrip.setActiveScene(0).play();
                this.setSceneAnimations(0);
            }
        }
        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._machine.spinStart(this._winAnimator.reset(false, true));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._machine.forceStop,
        );
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
        }
        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        this.visible = true;

        if (long) {
            if (this._winAnimator != null) {
                this._winAnimator.alpha = 0;
            }
            if (this._reelSwitch != null) {
                this._reelSwitch.goToCase("TRANSITION_TO_BASE");
                this._reelSwitch.alpha = 1;
                this._reelSwitch.scale.set(0.7, 0.7);
            }
        } else {
            if (this._reelSwitch != null) {
                this._reelSwitch.goToCase("NORMAL");
                this._reelSwitch.alpha = 1;
            }
        }

        if (this._winAnimator != null) {
            this._winAnimator.visible = true;
            Tweens.removeTweens(this._winAnimator);
            Tweens.alphaTween(this._winAnimator, {
                alphas: [this._winAnimator.alpha, 1],
                tweenDelay: 30,
                tweenDuration: 30,
            });
        }

        if (this._reelSwitch != null) {
            Tweens.removeTweens(this._reelSwitch);
            Tweens.scaleTween(this._reelSwitch, {
                tweenValues: [
                    { x: this._reelSwitch.scale.x, y: this._reelSwitch.scale.y } as PIXI.Point,
                    { x: 1, y: 1 } as PIXI.Point,
                ],
                tweenDuration: 30,
                tweenEase: "power4Out",
            });
        }

        if (this._backgroundStrip != null) {
            //dont turn off animations if we are in base game, and returning from gamble
            if (this._backgroundStrip.getLastScene() != 0) {
                this._backgroundStrip.setBackgroundAnimations(this._backgroundStrip.getLastScene(), false);
            }
            this._backgroundStrip.setActiveScene(0).play();
        }

        this.introFinished();
    }

    introFinished(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
        } else {
            PlayList.stop("SND_BaseRecaploop");
            PlayList.loop("SND_BaseLoop");
        }

        this._controller.introFinished();
    }

    /**
     * Delayed move to scene.
     */
    goToScene(): void {
        PlayList.stop("SND_FSTransitionSelect", 60);
        PlayList.play("SND_FSTransitionPrelet");
        if (
            GameManager.freespinsResponse.hasOwnProperty("customData") &&
            GameManager.freespinsResponse.customData["selectedSymbol"] != null
        ) {
            if (this._backgroundStrip != null) {
                let selectedSymbol = GameManager.freespinsResponse.customData["selectedSymbol"];
                let lastScene = this._backgroundStrip.getLastScene() ? this._backgroundStrip.getLastScene() : 0;

                this._backgroundStrip.goToScene(null, selectedSymbol, 240).play();
                this._backgroundStrip.lastAnimationBgScene = lastScene;
                this._backgroundStrip.setBackgroundAnimationsInInterval(lastScene, selectedSymbol, true);
                ModuleManager.setActiveSceneBackground("freespins", selectedSymbol);
            }
        }
    }

    outro(): void {
        if (ModuleManager.freespinsActive()) {
            if (
                GameManager.freespinsResponse.hasOwnProperty("customData") &&
                GameManager.freespinsResponse.customData["selectedSymbol"] != null
            ) {
                // Transition to the same scene or not
                if (
                    this._backgroundStrip != null &&
                    this._backgroundStrip.getLastScene() == GameManager.freespinsResponse.customData["selectedSymbol"]
                ) {
                    this._backgroundStrip.lastAnimationBgScene = this._backgroundStrip.getLastScene();
                    this._outroDelay.start(60);
                    PlayList.stop("SND_FSTransitionSelect", 60);
                } else {
                    this._outroDelay.start(300);
                    this._goToScene.start(60);

                    if (this._reelSwitch != null) {
                        Tweens.removeTweens(this._reelSwitch);
                        Tweens.scaleTween(this._reelSwitch, {
                            tweenValues: [{ x: 1, y: 1 } as PIXI.Point, { x: 0.7, y: 0.7 } as PIXI.Point],
                            tweenDuration: 60,
                            tweenEase: "power4Out",
                        });
                    }
                }

                if (this._reelSwitch != null) {
                    this._reelSwitch.goToCase(
                        "SELECTED_BUTTON_" + GameManager.freespinsResponse.customData["selectedSymbol"],
                    );
                }
            }
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");

            if (this._paylinesSelector != null) {
                this._paylinesSelector.reset();
            }

            let wasFinished: boolean = this._wasFinished;
            let inHighWins: boolean = this._winAnimator.reset(true);

            if (wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(60);
                } else {
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroFinished(): void {
        PlayList.stop("SND_FSTransitionPrelet", 120);
        if (ModuleManager.freespinsActive()) {
            this.visible = false;
        }

        //disable all scene animations between base and selected, disable also base, but not selected!
        if (this._backgroundStrip != null) {
            if (
                GameManager.freespinsResponse.hasOwnProperty("customData") &&
                GameManager.freespinsResponse.customData["selectedSymbol"] != null
            ) {
                this._backgroundStrip.setBackgroundAnimationsInInterval(
                    this._backgroundStrip.lastAnimationBgScene,
                    GameManager.freespinsResponse.customData["selectedSymbol"],
                    false,
                    false,
                );
            }
        }

        this._controller.outroFinished();
    }

    selectPayline(): void {}

    setSceneAnimations(sceneIndex: number): void {
        if (this._backgroundStrip != null) {
            this._backgroundStrip.setBackgroundAnimations(sceneIndex, true);
        }
    }

    /**
     * Show freespins configurator.
     */
    startConfigurator(): void {
        PlayList.stop("SND_FSBKGLoopGreen", 10);
        PlayList.stop("SND_FSBKGLoopBlue", 10);
        PlayList.stop("SND_FSBKGLoopWhite", 10);
        PlayList.stop("SND_FSBKGLoopRed", 10);
        PlayList.stop("SND_FSBKGLoopGold", 10);

        PlayList.stop("SND_BaseRecaploop", 10);
        PlayList.loop("SND_FSTransitionMenu");

        this.visible = true;

        if (this._reelSwitch != null) {
            this._reelSwitch.goToCase("CONFIGURATOR");
            this._reelSwitch.alpha = 1;
            this._reelSwitch.scale.set(1, 1);
        }

        if (ModuleManager.freespinsEnabled()) {
            // In case of retrigger
            this._winAnimator.alpha = 0;
            this._winAnimator.reset(true);
            if (
                GameManager.freespinsResponse.hasOwnProperty("customData") &&
                GameManager.freespinsResponse.customData["selectedSymbol"] != null
            ) {
                if (this._backgroundStrip != null) {
                    let selectedSymbol = GameManager.freespinsResponse.customData["selectedSymbol"];
                    this._backgroundStrip.setBackgroundAnimations(selectedSymbol, true);
                    this._backgroundStrip.setActiveScene(selectedSymbol).play();
                    this.setSceneAnimations(selectedSymbol);
                    ModuleManager.setActiveSceneBackground("freespins", selectedSymbol);
                }
            }
        } else {
            Tweens.removeTweens(this._winAnimator);
            Tweens.alphaTween(this._winAnimator, { alphas: [this._winAnimator.alpha, 0], tweenDuration: 30 });
        }
    }

    /**
     * Sets background of active scene.
     * @param {number} to
     */
    setActiveScene(to: number): void {
        this._backgroundStrip.setActiveScene(to).play();
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/